import { type ClubRole, RANK_ORDER } from "@rivaled/shared";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  Brain,
  Camera,
  ClipboardCheck,
  Crosshair,
  ExternalLink,
  Gauge,
  HelpCircle,
  ImageIcon,
  Info,
  Loader2,
  Puzzle,
  Save,
  Shield,
  ShieldBan,
  Sliders,
  Swords,
  Trash2,
  Trophy,
  Users,
  Workflow,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FeatureGate } from "../../../../../../components/FeatureGate";
import {
  Breadcrumbs,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  Input,
  Modal,
  ModalFooter,
  Select,
  Textarea,
} from "../../../../../../components/ui";
import { usePlatformRole } from "../../../../../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../../../../../lib/permissions";
import { trpc, type RouterOutputs } from "../../../../../../lib/trpc";
import { uploadFile } from "../../../../../../lib/uploadFile";

// ---------------------------------------------------------------------------
// Route with search param validation
// ---------------------------------------------------------------------------

const sectionIds = [
  "basic",
  "team",
  "restrictions",
  "match",
  "matchmaking",
  "ranks",
  "danger",
] as const;

type SectionId = (typeof sectionIds)[number];

export const Route = createFileRoute("/app/clubs/$slug/queues/$queueSlug/settings")({
  validateSearch: (search: Record<string, unknown>): { section: SectionId } => {
    const s = search.section;
    if (typeof s === "string" && (sectionIds as readonly string[]).includes(s)) {
      return { section: s as SectionId };
    }
    return { section: "basic" };
  },
  component: QueueSettingsPage,
});

// ---------------------------------------------------------------------------
// Nav items config
// ---------------------------------------------------------------------------

const navItems: { id: SectionId; label: string; icon: React.ReactNode; danger?: boolean }[] = [
  { id: "basic", label: "Basic Info", icon: <Info className="size-4" /> },
  { id: "team", label: "Team Config", icon: <Users className="size-4" /> },
  { id: "restrictions", label: "Restrictions", icon: <Shield className="size-4" /> },
  { id: "match", label: "Match Settings", icon: <Swords className="size-4" /> },
  { id: "matchmaking", label: "Matchmaking", icon: <Sliders className="size-4" /> },
  { id: "ranks", label: "Ranks", icon: <Trophy className="size-4" /> },
  { id: "danger", label: "Delete", icon: <Trash2 className="size-4" />, danger: true },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function QueueSettingsPage() {
  const { slug, queueSlug } = Route.useParams();
  const { section } = Route.useSearch();
  const navigate = useNavigate();
  const { isLoading: isPlatformRoleLoading, isModerator: isPlatformModerator } = usePlatformRole();

  const { data: myClubs, isLoading: myClubsLoading } = trpc.club.getMyClubs.useQuery();
  const { data: club, isLoading: clubLoading } = trpc.club.get.useQuery({ slug });
  const membership = myClubs?.find((c) => c.slug === slug);
  const clubId = membership?.id ?? club?.id;
  const isAdmin =
    canClubRolePermission(membership?.role as ClubRole | undefined, {
      resource: "queue",
      actions: ["update"],
    }) || isPlatformModerator;

  const { data: queue, isLoading } = trpc.queue.get.useQuery({ clubSlug: slug, queueSlug });
  const queueWithConfig = queue as
    | (NonNullable<RouterOutputs["queue"]["get"]> & {
        matchmakingConfig?: Record<string, Record<string, number | undefined>> | null;
      })
    | undefined;
  const queueId = queue?.id ?? "";
  const utils = trpc.useUtils();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [teamSize, setTeamSize] = useState<string>("");
  const [teamCount, setTeamCount] = useState<string>("2");
  const [allowFullTeams, setAllowFullTeams] = useState(true);
  const [eloMin, setEloMin] = useState<string>("");
  const [eloMax, setEloMax] = useState<string>("");
  const [maxPartySize, setMaxPartySize] = useState<string>("");
  const [partyEloDisparity, setPartyEloDisparity] = useState<string>("");
  const [acceptTimeout, setAcceptTimeout] = useState<string>("");
  const [noShowTimeout, setNoShowTimeout] = useState<string>("");
  const [hostSelection, setHostSelection] = useState<string>("");
  const [teamFormation, setTeamFormation] = useState<string>("");
  const [bannerUrl, setBannerUrl] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [pendingBannerFile, setPendingBannerFile] = useState<File | null>(null);
  const [pendingIconFile, setPendingIconFile] = useState<File | null>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  // Rank config
  const [rankThresholds, setRankThresholds] = useState<Record<string, string>>({});
  const [primeTopN, setPrimeTopN] = useState<string>("");
  const [championTopN, setChampionTopN] = useState<string>("");

  // Matchmaking config
  const [mmEloBaseRange, setMmEloBaseRange] = useState<string>("");
  const [mmEloExpansionStep, setMmEloExpansionStep] = useState<string>("");
  const [mmEloMaxDisparity, setMmEloMaxDisparity] = useState<string>("");
  const [mmEloExpansionInterval, setMmEloExpansionInterval] = useState<string>("");
  const [mmMinMatchQuality, setMmMinMatchQuality] = useState<string>("");
  const [mmScoringEloWeight, setMmScoringEloWeight] = useState<string>("");
  const [mmScoringWaitWeight, setMmScoringWaitWeight] = useState<string>("");
  const [mmQualityDivisor, setMmQualityDivisor] = useState<string>("");
  const [mmPartyHighestWeight, setMmPartyHighestWeight] = useState<string>("");
  const [mmPartyAverageWeight, setMmPartyAverageWeight] = useState<string>("");
  const [mmTsDefaultMu, setMmTsDefaultMu] = useState<string>("");
  const [mmTsDefaultSigma, setMmTsDefaultSigma] = useState<string>("");
  const [mmTsBeta, setMmTsBeta] = useState<string>("");
  const [mmTsTau, setMmTsTau] = useState<string>("");
  const [mmTsDrawProbability, setMmTsDrawProbability] = useState<string>("");
  const [mmTsDisplayEloMultiplier, setMmTsDisplayEloMultiplier] = useState<string>("");
  const [mmBansBaseDuration, setMmBansBaseDuration] = useState<string>("");
  const [mmBansEscalation, setMmBansEscalation] = useState<string>("");
  const [mmBansHistoryWindow, setMmBansHistoryWindow] = useState<string>("");
  const [mmBansMaxDuration, setMmBansMaxDuration] = useState<string>("");
  const [mmReportingTimeout, setMmReportingTimeout] = useState<string>("");

  // Composition config
  const [compositionSlots, setCompositionSlots] = useState<{ role: string; count: string }[]>([]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [helpSection, setHelpSection] = useState<SectionId | null>(null);

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    setPreview: (url: string | null) => void,
    currentPreview: string | null,
    setPendingFile: (f: File | null) => void,
    maxSize: number,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use PNG, JPEG, or WebP.");
      return;
    }
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${maxSize / (1024 * 1024)} MB.`);
      return;
    }

    if (currentPreview) URL.revokeObjectURL(currentPreview);
    setPreview(URL.createObjectURL(file));
    setPendingFile(file);
  };

  const doUpload = async (file: File, category: "queue-icon" | "queue-banner"): Promise<string> => {
    const { publicUrl } = await uploadFile(file, category, queueId);
    if (!publicUrl) throw new Error("Upload did not return a public URL");
    return publicUrl;
  };

  // Populate form when queue loads
  useEffect(() => {
    if (!queue) return;
    setName(queue.name);
    setDescription(queue.description ?? "");
    setEnabled(queue.isEnabled);
    setTeamSize(queue.teamSize?.toString() ?? "");
    setTeamCount(queue.teamCount?.toString() ?? "2");
    setAllowFullTeams(queue.allowFullTeams ?? true);
    setEloMin(queue.eloMin?.toString() ?? "");
    setEloMax(queue.eloMax?.toString() ?? "");
    setMaxPartySize(queue.maxPartySize?.toString() ?? "");
    setPartyEloDisparity(queue.partyEloDisparity?.toString() ?? "");
    setAcceptTimeout(queue.acceptTimeout?.toString() ?? "");
    setNoShowTimeout(queue.noShowTimeout?.toString() ?? "");
    setHostSelection(queue.hostSelection ?? "");
    setTeamFormation(queue.teamFormation ?? "");
    setBannerUrl(queue.bannerUrl ?? "");
    setIconUrl(queue.iconUrl ?? "");

    // Rank config
    const rc = queue.rankConfig as {
      thresholds?: Record<string, number>;
      primeTopN?: number | null;
      championTopN?: number | null;
    } | null;
    if (rc?.thresholds) {
      setRankThresholds(
        Object.fromEntries(Object.entries(rc.thresholds).map(([k, v]) => [k, v.toString()])),
      );
    }
    setPrimeTopN(rc?.primeTopN?.toString() ?? "");
    setChampionTopN(rc?.championTopN?.toString() ?? "");

    // Matchmaking config
    const mc = queueWithConfig?.matchmakingConfig ?? null;
    if (mc) {
      setMmEloBaseRange(mc.eloWindow?.baseRange?.toString() ?? "");
      setMmEloExpansionStep(mc.eloWindow?.expansionStep?.toString() ?? "");
      setMmEloMaxDisparity(mc.eloWindow?.maxDisparity?.toString() ?? "");
      setMmEloExpansionInterval(mc.eloWindow?.expansionIntervalSeconds?.toString() ?? "");
      setMmMinMatchQuality(mc.quality?.minMatchQuality?.toString() ?? "");
      setMmScoringEloWeight(mc.quality?.scoringEloWeight?.toString() ?? "");
      setMmScoringWaitWeight(mc.quality?.scoringWaitWeight?.toString() ?? "");
      setMmQualityDivisor(mc.quality?.qualityDivisor?.toString() ?? "");
      setMmPartyHighestWeight(mc.party?.ratingHighestWeight?.toString() ?? "");
      setMmPartyAverageWeight(mc.party?.ratingAverageWeight?.toString() ?? "");
      setMmTsDefaultMu(mc.trueskill?.defaultMu?.toString() ?? "");
      setMmTsDefaultSigma(mc.trueskill?.defaultSigma?.toString() ?? "");
      setMmTsBeta(mc.trueskill?.beta?.toString() ?? "");
      setMmTsTau(mc.trueskill?.tau?.toString() ?? "");
      setMmTsDrawProbability(mc.trueskill?.drawProbability?.toString() ?? "");
      setMmTsDisplayEloMultiplier(mc.trueskill?.displayEloMultiplier?.toString() ?? "");
      setMmBansBaseDuration(mc.bans?.baseDurationSeconds?.toString() ?? "");
      setMmBansEscalation(mc.bans?.escalationMultiplier?.toString() ?? "");
      setMmBansHistoryWindow(mc.bans?.historyWindowHours?.toString() ?? "");
      setMmBansMaxDuration(mc.bans?.maxBanDurationSeconds?.toString() ?? "");
      setMmReportingTimeout(mc.reporting?.timeoutSeconds?.toString() ?? "");

      // Composition
      const comp = mc.composition as { slots?: { role: string; count: number }[] } | undefined;
      if (comp?.slots?.length) {
        setCompositionSlots(comp.slots.map((s) => ({ role: s.role, count: s.count.toString() })));
      } else {
        setCompositionSlots([]);
      }
    }
  }, [queue]);

  const updateMutation = trpc.queue.update.useMutation({
    onSuccess: (data) => {
      toast.success("Queue settings saved");
      void utils.queue.get.invalidate();
      void utils.queue.list.invalidate();
      if (data.slug && data.slug !== queueSlug) {
        void navigate({
          to: "/app/clubs/$slug/queues/$queueSlug/settings",
          params: { slug, queueSlug: data.slug },
          search: { section },
        });
      }
    },
    onError: (err) => toast.error(err.message || "Failed to save"),
  });

  const deleteMutation = trpc.queue.delete.useMutation({
    onSuccess: () => {
      toast.success("Queue deleted");
      void navigate({ to: "/app/clubs/$slug", params: { slug } });
    },
    onError: (err) => toast.error(err.message || "Failed to delete queue"),
  });

  const handleSave = async () => {
    if (!clubId) return;
    setIsSaving(true);

    try {
      const optNum = (v: string) => (v ? Number(v) : undefined);
      const optNumNull = (v: string) => (v ? Number(v) : null);

      // Upload pending files
      let newBannerUrl = bannerUrl || null;
      let newIconUrl = iconUrl || null;

      if (pendingBannerFile) {
        newBannerUrl = await doUpload(pendingBannerFile, "queue-banner");
      }
      if (pendingIconFile) {
        newIconUrl = await doUpload(pendingIconFile, "queue-icon");
      }

      // Build matchmaking config
      const matchmakingConfig = {
        eloWindow: {
          baseRange: optNum(mmEloBaseRange),
          expansionStep: optNum(mmEloExpansionStep),
          maxDisparity: optNum(mmEloMaxDisparity),
          expansionIntervalSeconds: optNum(mmEloExpansionInterval),
        },
        quality: {
          minMatchQuality: optNum(mmMinMatchQuality),
          scoringEloWeight: optNum(mmScoringEloWeight),
          scoringWaitWeight: optNum(mmScoringWaitWeight),
          qualityDivisor: optNum(mmQualityDivisor),
        },
        party: {
          ratingHighestWeight: optNum(mmPartyHighestWeight),
          ratingAverageWeight: optNum(mmPartyAverageWeight),
        },
        trueskill: {
          defaultMu: optNum(mmTsDefaultMu),
          defaultSigma: optNum(mmTsDefaultSigma),
          beta: optNum(mmTsBeta),
          tau: optNum(mmTsTau),
          drawProbability: optNum(mmTsDrawProbability),
          displayEloMultiplier: optNum(mmTsDisplayEloMultiplier),
        },
        bans: {
          baseDurationSeconds: optNum(mmBansBaseDuration),
          escalationMultiplier: optNum(mmBansEscalation),
          historyWindowHours: optNum(mmBansHistoryWindow),
          maxBanDurationSeconds: optNum(mmBansMaxDuration),
        },
        reporting: {
          timeoutSeconds: optNum(mmReportingTimeout),
        },
        composition:
          compositionSlots.length > 0
            ? {
                slots: compositionSlots
                  .filter((s) => s.role.trim())
                  .map((s) => ({ role: s.role.trim(), count: Number(s.count) || 1 })),
              }
            : null,
      };

      // Build rank config
      const thresholds = Object.fromEntries(
        Object.entries(rankThresholds)
          .filter(([, v]) => v !== "")
          .map(([k, v]) => [k, Number(v)]),
      );

      await updateMutation.mutateAsync({
        queueId,
        clubId,
        name: name.trim(),
        description: description.trim() || undefined,
        teamSize: optNum(teamSize),
        matchSize: computedMatchSize > 0 ? computedMatchSize : undefined,
        teamCount: optNum(teamCount),
        allowFullTeams,
        eloMin: optNumNull(eloMin),
        eloMax: optNumNull(eloMax),
        maxPartySize: optNumNull(maxPartySize),
        partyEloDisparity: optNumNull(partyEloDisparity),
        acceptTimeout: optNumNull(acceptTimeout),
        noShowTimeout: optNumNull(noShowTimeout),
        hostSelection: (hostSelection || null) as "first_accept" | "highest_elo" | "random" | null,
        teamFormation: (teamFormation || null) as
          | "auto_balance"
          | "captains"
          | "random"
          | "self_select"
          | null,
        matchmakingConfig,
        rankConfig: {
          thresholds: Object.keys(thresholds).length > 0 ? thresholds : undefined,
          primeTopN: primeTopN ? Number(primeTopN) : null,
          championTopN: championTopN ? Number(championTopN) : null,
        },
        bannerUrl: newBannerUrl,
        iconUrl: newIconUrl,
      });

      setPendingBannerFile(null);
      setPendingIconFile(null);

      // Also toggle enabled if changed
      if (queue && enabled !== queue.isEnabled) {
        await setEnabledMutation.mutateAsync({ queueId, clubId, enabled });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const setEnabledMutation = trpc.queue.setEnabled.useMutation({
    onSuccess: () => {
      void utils.queue.get.invalidate({ queueId });
    },
  });

  const setSection = (id: SectionId) => {
    void navigate({
      to: "/app/clubs/$slug/queues/$queueSlug/settings",
      params: { slug, queueSlug },
      search: { section: id },
      replace: true,
    });
  };

  // Access check
  if (
    !isLoading &&
    !myClubsLoading &&
    !clubLoading &&
    !isPlatformRoleLoading &&
    (!isAdmin || !clubId)
  ) {
    void navigate({ to: "/app/clubs/$slug", params: { slug } });
    return null;
  }

  if (isLoading || myClubsLoading || clubLoading || isPlatformRoleLoading || !queue) {
    return (
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-6">
        <div className="h-8 w-48 rounded-lg bg-surface-700/40 animate-pulse" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 rounded-xl bg-surface-800/50 animate-pulse" />
        ))}
      </div>
    );
  }

  const saving = isSaving || updateMutation.isPending || setEnabledMutation.isPending;

  // ---------------------------------------------------------------------------
  // Section content renderers
  // ---------------------------------------------------------------------------

  const hint = (text: string) => <p className="text-xs text-surface-500 mt-1">{text}</p>;

  const renderBasic = () => (
    <div className="space-y-4">
      <Input
        label="Queue Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={100}
      />
      <Textarea
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        maxLength={500}
        rows={2}
      />
      <div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-surface-300">Enabled</label>
          <button
            type="button"
            onClick={() => setEnabled((e) => !e)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              enabled ? "bg-brand-500" : "bg-surface-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-surface-500 mt-1">
          When disabled, players cannot join this queue.
        </p>
      </div>

      {/* Banner */}
      <div>
        <label className="block text-xs font-medium text-surface-400 mb-1.5">Banner</label>
        <button
          type="button"
          className="relative w-full h-32 rounded-xl overflow-hidden border border-dashed border-surface-600 hover:border-brand-500/40 transition-colors group cursor-pointer"
          onClick={() => bannerInputRef.current?.click()}
        >
          {bannerPreview || bannerUrl ? (
            <>
              <img
                src={bannerPreview ?? bannerUrl}
                alt="Queue banner"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Camera className="size-6 text-white" />
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-1.5 text-surface-500 group-hover:text-brand-400 transition-colors">
              <ImageIcon className="size-6" />
              <span className="text-xs">Upload banner</span>
            </div>
          )}
        </button>
        <input
          ref={bannerInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) =>
            handleFileSelect(
              e,
              setBannerPreview,
              bannerPreview,
              setPendingBannerFile,
              5 * 1024 * 1024,
            )
          }
        />
      </div>

      {/* Icon */}
      <div>
        <label className="block text-xs font-medium text-surface-400 mb-1.5">Icon</label>
        <div className="flex items-center gap-4">
          <button
            type="button"
            className="relative w-16 h-16 rounded-xl overflow-hidden border border-dashed border-surface-600 hover:border-brand-500/40 transition-colors group cursor-pointer shrink-0"
            onClick={() => iconInputRef.current?.click()}
          >
            {iconPreview || iconUrl ? (
              <>
                <img
                  src={iconPreview ?? iconUrl}
                  alt="Queue icon"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera className="size-5 text-white" />
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-surface-500 group-hover:text-brand-400 transition-colors">
                <Camera className="size-5" />
              </div>
            )}
          </button>
          <p className="text-xs text-surface-500">
            {iconPreview || iconUrl ? "Click to change" : "Upload a queue icon"}
          </p>
        </div>
        <input
          ref={iconInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={(e) =>
            handleFileSelect(e, setIconPreview, iconPreview, setPendingIconFile, 2 * 1024 * 1024)
          }
        />
      </div>
    </div>
  );

  const computedMatchSize = (Number(teamSize) || 0) * (Number(teamCount) || 0);

  const renderTeam = () => (
    <div className="space-y-4">
      {hint("Configure how teams are structured for matches in this queue.")}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Input
            label="Team Size"
            type="number"
            value={teamSize}
            onChange={(e) => setTeamSize(e.target.value)}
            placeholder="e.g. 5"
            min={1}
            max={50}
          />
          {hint("Players per team.")}
        </div>
        <div className="space-y-1">
          <Input
            label="Team Count"
            type="number"
            value={teamCount}
            onChange={(e) => setTeamCount(e.target.value)}
            min={1}
          />
          {hint("Number of teams per match.")}
        </div>
      </div>
      {computedMatchSize > 0 &&
        (() => {
          const ts = Number(teamSize);
          const tc = Number(teamCount);
          const isFFA = ts === 1;
          const format = isFFA
            ? `${tc}-player FFA`
            : tc <= 3
              ? Array(tc).fill(ts).join("v")
              : `${ts} × ${tc}`;
          return (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-700/30 border border-surface-700">
              <span className="text-xs text-surface-400">Format:</span>
              <span className="text-sm font-bold text-white">{format}</span>
              <span className="text-xs text-surface-500">({computedMatchSize} players)</span>
            </div>
          );
        })()}
      <div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-surface-300">Allow Full Teams</label>
          <button
            type="button"
            onClick={() => setAllowFullTeams((v) => !v)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              allowFullTeams ? "bg-brand-500" : "bg-surface-600"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                allowFullTeams ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
        <p className="text-xs text-surface-500 mt-1">
          Allow pre-made groups that fill an entire team to queue together.
        </p>
      </div>
    </div>
  );

  const renderRestrictions = () => (
    <div className="space-y-4">
      {hint("Limit who can join this queue based on rating and party composition.")}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1 opacity-50">
          <Input
            label="Min ELO"
            type="number"
            value=""
            onChange={() => {}}
            placeholder="Coming soon"
            disabled
          />
          {hint("ELO restrictions are being reworked.")}
        </div>
        <div className="space-y-1 opacity-50">
          <Input
            label="Max ELO"
            type="number"
            value=""
            onChange={() => {}}
            placeholder="Coming soon"
            disabled
          />
          {hint("ELO restrictions are being reworked.")}
        </div>
        <div className="space-y-1">
          <Input
            label="Max Party Size"
            type="number"
            value={maxPartySize}
            onChange={(e) => setMaxPartySize(e.target.value)}
            placeholder="Team size"
            min={1}
          />
          {hint("Max players that can queue together as a group.")}
        </div>
        <div className="space-y-1">
          <Input
            label="Party ELO Disparity"
            type="number"
            value={partyEloDisparity}
            onChange={(e) => setPartyEloDisparity(e.target.value)}
            placeholder="Unlimited"
            min={0}
          />
          {hint("Max rating gap allowed between party members.")}
        </div>
      </div>
    </div>
  );

  const renderMatch = () => (
    <div className="space-y-4">
      {hint("Control match flow from acceptance through to team assignment.")}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Input
            label="Accept Timeout (s)"
            type="number"
            value={acceptTimeout}
            onChange={(e) => setAcceptTimeout(e.target.value)}
            placeholder="Default"
            min={10}
            max={120}
          />
          {hint("How long players have to accept a found match.")}
        </div>
        <div className="space-y-1">
          <Input
            label="No-Show Timeout (s)"
            type="number"
            value={noShowTimeout}
            onChange={(e) => setNoShowTimeout(e.target.value)}
            placeholder="Default"
            min={60}
            max={3600}
          />
          {hint("Time before a player who accepted is marked absent.")}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-surface-400 mb-1.5">
            Host Selection
          </label>
          <Select
            options={[
              { value: "", label: "Default" },
              { value: "random", label: "Random" },
              { value: "highest_elo", label: "Highest ELO" },
              { value: "volunteer", label: "Volunteer" },
            ]}
            value={hostSelection}
            onChange={setHostSelection}
            className="w-full"
          />
          <p className="text-xs text-surface-500 mt-1">How the match host is chosen.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-surface-400 mb-1.5">
            Team Formation
          </label>
          <Select
            options={[
              { value: "", label: "Default" },
              { value: "balanced", label: "Balanced" },
              { value: "captains", label: "Captains" },
              { value: "random", label: "Random" },
            ]}
            value={teamFormation}
            onChange={setTeamFormation}
            className="w-full"
          />
          <p className="text-xs text-surface-500 mt-1">How players are assigned to teams.</p>
        </div>
      </div>
    </div>
  );

  const renderMatchmaking = () => (
    <div className="space-y-4">
      {hint(
        "Fine-tune the matchmaking algorithm. Leave blank to use system defaults — only change these if you understand the impact.",
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        {/* ELO Window */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <Crosshair className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">ELO Window</h4>
              <p className="text-xs text-surface-500">
                Rating range that expands over time to reduce wait.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Base Range"
              type="number"
              value={mmEloBaseRange}
              onChange={(e) => setMmEloBaseRange(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Expansion Step"
              type="number"
              value={mmEloExpansionStep}
              onChange={(e) => setMmEloExpansionStep(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Max Disparity"
              type="number"
              value={mmEloMaxDisparity}
              onChange={(e) => setMmEloMaxDisparity(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Expansion Interval (s)"
              type="number"
              value={mmEloExpansionInterval}
              onChange={(e) => setMmEloExpansionInterval(e.target.value)}
              placeholder="Default"
            />
          </div>
        </div>

        {/* Match Quality */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <Gauge className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Match Quality</h4>
              <p className="text-xs text-surface-500">ELO weight vs wait weight tradeoff.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Min Match Quality"
              type="number"
              step="0.01"
              value={mmMinMatchQuality}
              onChange={(e) => setMmMinMatchQuality(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="ELO Weight"
              type="number"
              step="0.01"
              value={mmScoringEloWeight}
              onChange={(e) => setMmScoringEloWeight(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Wait Weight"
              type="number"
              step="0.01"
              value={mmScoringWaitWeight}
              onChange={(e) => setMmScoringWaitWeight(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Quality Divisor"
              type="number"
              value={mmQualityDivisor}
              onChange={(e) => setMmQualityDivisor(e.target.value)}
              placeholder="Default"
            />
          </div>
        </div>

        {/* Party Rating */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <Users className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Party Rating</h4>
              <p className="text-xs text-surface-500">
                Blend of highest-rated member and party average.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Highest Weight"
              type="number"
              step="0.01"
              value={mmPartyHighestWeight}
              onChange={(e) => setMmPartyHighestWeight(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Average Weight"
              type="number"
              step="0.01"
              value={mmPartyAverageWeight}
              onChange={(e) => setMmPartyAverageWeight(e.target.value)}
              placeholder="Default"
            />
          </div>
        </div>

        {/* TrueSkill */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <Brain className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">TrueSkill</h4>
              <p className="text-xs text-surface-500">Mu, sigma, beta, and tau parameters.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Default Mu"
              type="number"
              step="0.1"
              value={mmTsDefaultMu}
              onChange={(e) => setMmTsDefaultMu(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Default Sigma"
              type="number"
              step="0.1"
              value={mmTsDefaultSigma}
              onChange={(e) => setMmTsDefaultSigma(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Beta"
              type="number"
              step="0.1"
              value={mmTsBeta}
              onChange={(e) => setMmTsBeta(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Tau"
              type="number"
              step="0.01"
              value={mmTsTau}
              onChange={(e) => setMmTsTau(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Draw Probability"
              type="number"
              step="0.01"
              value={mmTsDrawProbability}
              onChange={(e) => setMmTsDrawProbability(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Display ELO Multiplier"
              type="number"
              value={mmTsDisplayEloMultiplier}
              onChange={(e) => setMmTsDisplayEloMultiplier(e.target.value)}
              placeholder="Default"
            />
          </div>
        </div>

        {/* Bans */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <ShieldBan className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Bans</h4>
              <p className="text-xs text-surface-500">
                Auto bans for dodging. Escalates with repeat offenses.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Base Duration (s)"
              type="number"
              value={mmBansBaseDuration}
              onChange={(e) => setMmBansBaseDuration(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Escalation Multiplier"
              type="number"
              step="0.1"
              value={mmBansEscalation}
              onChange={(e) => setMmBansEscalation(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="History Window (h)"
              type="number"
              value={mmBansHistoryWindow}
              onChange={(e) => setMmBansHistoryWindow(e.target.value)}
              placeholder="Default"
            />
            <Input
              label="Max Ban Duration (s)"
              type="number"
              value={mmBansMaxDuration}
              onChange={(e) => setMmBansMaxDuration(e.target.value)}
              placeholder="Default"
            />
          </div>
        </div>

        {/* Reporting */}
        <div className="space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <ClipboardCheck className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Reporting</h4>
              <p className="text-xs text-surface-500">Time limit for submitting match results.</p>
            </div>
          </div>
          <Input
            label="Timeout (s)"
            type="number"
            value={mmReportingTimeout}
            onChange={(e) => setMmReportingTimeout(e.target.value)}
            placeholder="Default"
          />
        </div>

        {/* Composition */}
        <div className="space-y-3 lg:col-span-2">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-surface-800 flex items-center justify-center">
              <Puzzle className="w-3.5 h-3.5 text-brand-400" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">Composition</h4>
              <p className="text-xs text-surface-500">
                Required roles per team (e.g. 1 Tank, 1 Healer, 3 DPS).
              </p>
            </div>
          </div>
          {compositionSlots.length === 0 ? (
            <p className="text-xs text-surface-500 italic">
              No role requirements — any player can fill any slot.
            </p>
          ) : (
            <div className="space-y-2">
              {compositionSlots.map((slot, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    placeholder="Role name"
                    value={slot.role}
                    onChange={(e) => {
                      const next = [...compositionSlots];
                      next[i] = { ...next[i], role: e.target.value };
                      setCompositionSlots(next);
                    }}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={slot.count}
                    onChange={(e) => {
                      const next = [...compositionSlots];
                      next[i] = { ...next[i], count: e.target.value };
                      setCompositionSlots(next);
                    }}
                    className="w-20"
                  />
                  <button
                    type="button"
                    onClick={() => setCompositionSlots((s) => s.filter((_, j) => j !== i))}
                    className="p-1.5 rounded-md text-surface-500 hover:text-error-400 hover:bg-error-500/10 transition-colors"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
              {(() => {
                const total = compositionSlots.reduce((sum, s) => sum + (Number(s.count) || 0), 0);
                const ts = Number(teamSize) || 0;
                if (ts > 0 && total > 0 && total !== ts) {
                  return (
                    <p className={`text-xs ${total > ts ? "text-error-400" : "text-amber-400"}`}>
                      Slot total ({total}) {total > ts ? "exceeds" : "is less than"} team size ({ts}
                      )
                    </p>
                  );
                }
                return null;
              })()}
            </div>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCompositionSlots((s) => [...s, { role: "", count: "1" }])}
          >
            + Add Role
          </Button>
        </div>
      </div>
    </div>
  );

  const renderRanks = () => (
    <div className="space-y-4">
      {hint(
        "Set the minimum ELO required for each rank tier. Players are assigned the highest rank whose threshold they meet. Leave blank for system defaults.",
      )}
      <div className="grid grid-cols-2 gap-3">
        {RANK_ORDER.map((tier) => (
          <Input
            key={tier}
            label={tier.charAt(0).toUpperCase() + tier.slice(1)}
            type="number"
            value={rankThresholds[tier] ?? ""}
            onChange={(e) => setRankThresholds((prev) => ({ ...prev, [tier]: e.target.value }))}
            placeholder="Default"
          />
        ))}
      </div>
      <div className="border-t border-separator pt-4 space-y-3">
        <p className="text-xs text-surface-500">
          Prime and Champion are special ranks awarded to the top N players by rating, regardless of
          thresholds.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Prime Top N"
            type="number"
            value={primeTopN}
            onChange={(e) => setPrimeTopN(e.target.value)}
            placeholder="Default"
            min={1}
          />
          <Input
            label="Champion Top N"
            type="number"
            value={championTopN}
            onChange={(e) => setChampionTopN(e.target.value)}
            placeholder="Default"
            min={1}
          />
        </div>
      </div>
    </div>
  );

  const renderDanger = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-surface-200">Delete Queue</p>
          <p className="text-xs text-surface-500">
            This action cannot be undone. All queue data will be lost.
          </p>
        </div>
        <Button
          variant="outline"
          size="md"
          leftIcon={<Trash2 className="size-4" />}
          onClick={() => setShowDeleteModal(true)}
          className="!border-error-500/30 !text-error-400 hover:!bg-error-500/10"
        >
          Delete
        </Button>
      </div>
    </div>
  );

  const withGate = (render: () => React.ReactNode) => () => (
    <FeatureGate clubId={clubId!} slug={slug}>
      {render()}
    </FeatureGate>
  );

  const sectionRenderers: Record<SectionId, () => React.ReactNode> = {
    basic: renderBasic,
    team: renderTeam,
    restrictions: withGate(renderRestrictions),
    match: withGate(renderMatch),
    matchmaking: withGate(renderMatchmaking),
    ranks: renderRanks,
    danger: renderDanger,
  };

  const sectionTitles: Record<SectionId, string> = {
    basic: "Basic Info",
    team: "Team Config",
    restrictions: "Restrictions",
    match: "Match Settings",
    matchmaking: "Advanced Matchmaking",
    ranks: "Rank Config",
    danger: "Delete",
  };

  const sectionDocs: Record<SectionId, React.ReactNode> = {
    basic: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          Configure the queue's display name, description, banner, and icon. These are shown to
          players browsing available queues.
        </p>
        <p>
          The <span className="text-white font-medium">Enabled</span> toggle controls whether
          players can join this queue. Disabling it won't affect matches already in progress.
        </p>
      </div>
    ),
    team: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          <span className="text-white font-medium">Team Size</span> is the number of players per
          team. <span className="text-white font-medium">Team Count</span> is how many teams compete
          in each match.
        </p>
        <p>
          For a standard 5v5, set team size to 5 and team count to 2. For a 10-player FFA, set team
          size to 1 and team count to 10.
        </p>
        <p>
          <span className="text-white font-medium">Allow Full Teams</span> lets pre-made groups that
          fill an entire team slot queue together. Disabling this forces solo or partial-party
          queuing only.
        </p>
      </div>
    ),
    restrictions: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          <span className="text-white font-medium">Max Party Size</span> caps how many players can
          queue together as a group. Set it to 1 for solo-only queues.
        </p>
        <p>
          <span className="text-white font-medium">Party ELO Disparity</span> limits the rating gap
          between the highest and lowest rated members of a party. This prevents high-rated players
          from boosting low-rated friends. Leave blank for no restriction.
        </p>
      </div>
    ),
    match: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          <span className="text-white font-medium">Accept Timeout</span> — when a match is found,
          all players must accept within this window. If anyone declines or times out, the remaining
          players are re-queued.
        </p>
        <p>
          <span className="text-white font-medium">No-Show Timeout</span> — after accepting, players
          must join the game server within this time or they're marked absent and may receive a
          queue ban.
        </p>
        <h4 className="text-white font-medium pt-1">Host Selection</h4>
        <ul className="list-disc list-inside space-y-1 text-surface-400">
          <li>
            <span className="text-surface-300">Random</span> — a random player is chosen as host
          </li>
          <li>
            <span className="text-surface-300">Highest ELO</span> — the highest rated player hosts
          </li>
          <li>
            <span className="text-surface-300">Volunteer</span> — players can volunteer; random
            fallback
          </li>
        </ul>
        <h4 className="text-white font-medium pt-1">Team Formation</h4>
        <ul className="list-disc list-inside space-y-1 text-surface-400">
          <li>
            <span className="text-surface-300">Balanced</span> — algorithm distributes players for
            even teams
          </li>
          <li>
            <span className="text-surface-300">Captains</span> — top-rated players draft their teams
          </li>
          <li>
            <span className="text-surface-300">Random</span> — fully random team assignment
          </li>
        </ul>
      </div>
    ),
    matchmaking: (
      <div className="space-y-4 text-sm text-surface-300">
        <p>
          These are advanced parameters that control the matchmaking algorithm. Leave them at
          defaults unless you have a specific reason to change them.
        </p>
        <div>
          <h4 className="text-white font-medium mb-1">ELO Window</h4>
          <p>
            The matchmaker searches for opponents within a rating window. It starts at{" "}
            <span className="text-white">Base Range</span> (e.g. ±100 ELO) and expands by{" "}
            <span className="text-white">Expansion Step</span> every{" "}
            <span className="text-white">Expansion Interval</span> seconds until{" "}
            <span className="text-white">Max Disparity</span> is reached. Wider windows find matches
            faster but sacrifice quality.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">Match Quality</h4>
          <p>
            Quality score = (ELO closeness × ELO Weight + wait time × Wait Weight) / Quality
            Divisor. Matches below <span className="text-white">Min Match Quality</span> are
            rejected. Higher ELO weight favors skill balance; higher wait weight reduces queue
            times.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">Party Rating</h4>
          <p>
            A party's effective rating is a weighted blend: (highest member's rating × Highest
            Weight) + (party average × Average Weight). The two weights should sum to 1. Weighting
            toward the highest player penalizes boosting.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">TrueSkill</h4>
          <p>
            <span className="text-white">Mu (μ)</span> is a player's estimated skill,{" "}
            <span className="text-white">Sigma (σ)</span> is uncertainty. New players start at
            Default Mu ± Default Sigma. <span className="text-white">Beta (β)</span> controls the
            performance variance — higher means more luck-dependent.{" "}
            <span className="text-white">Tau (τ)</span> adds uncertainty each game to allow ratings
            to drift over time. <span className="text-white">Display ELO Multiplier</span> converts
            internal TrueSkill values to a user-facing ELO number.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">Bans</h4>
          <p>
            Players who dodge or no-show receive automatic queue bans. Ban duration = Base Duration
            × Escalation Multiplier ^ (recent offenses - 1). Offenses are counted within the{" "}
            <span className="text-white">History Window</span>. The formula caps at{" "}
            <span className="text-white">Max Ban Duration</span>.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">Reporting</h4>
          <p>
            After a match ends, players have this many seconds to submit results. If the timeout
            expires, the match may be auto-resolved or flagged for admin review.
          </p>
        </div>
        <div>
          <h4 className="text-white font-medium mb-1">Composition</h4>
          <p>
            Define required roles per team. Each slot specifies a role name and how many of that
            role are needed. For example, a 5v5 team might require 1 Tank, 1 Healer, and 3 DPS. The
            sum of all slot counts should equal the team size. When composition is set, players must
            select a role when queuing and the matchmaker will fill each role requirement before
            forming a match.
          </p>
        </div>
      </div>
    ),
    ranks: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          Ranks are assigned based on ELO thresholds. A player gets the highest rank whose threshold
          they meet or exceed.
        </p>
        <p>For example, if Gold = 1200 and Platinum = 1500, a player with 1350 ELO is Gold.</p>
        <p>
          <span className="text-white font-medium">Prime</span> and{" "}
          <span className="text-white font-medium">Champion</span> are special override ranks
          awarded to the top N players by rating, regardless of their threshold-based rank. If Prime
          Top N = 50, the top 50 players are Prime. Champion Top N works the same way but for a
          smaller elite group.
        </p>
      </div>
    ),
    danger: (
      <div className="space-y-3 text-sm text-surface-300">
        <p>
          Deleting a queue permanently removes all associated data including match history, player
          stats, ELO ratings, and rank assignments for this queue.
        </p>
        <p>This action cannot be undone. Players will lose all progress in this queue.</p>
      </div>
    ),
  };

  return (
    <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-5">
      {/* Header */}
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            {
              label: membership?.name ?? club?.name ?? "Club",
              to: "/app/clubs/$slug",
              params: { slug },
            },
            {
              label: queue.name,
              to: "/app/clubs/$slug/queues/$queueSlug",
              params: { slug, queueSlug },
            },
            { label: "Settings" },
          ]}
        />
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Queue Settings</h1>
          <Button
            variant="primary"
            size="md"
            leftIcon={
              saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />
            }
            disabled={saving || !name.trim()}
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
      {/* Mobile nav strip */}
      <div className="lg:hidden overflow-x-auto scrollbar-none -mx-4 px-4">
        <div className="flex gap-1 min-w-max pb-1">
          {navItems.map((item) => (
            <React.Fragment key={item.id}>
              {item.danger && (
                <a
                  href={`/app/clubs/${slug}/queues/${queueSlug}/flow-editor`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                >
                  <Workflow className="size-4" />
                  Flow Editor
                  <ExternalLink className="size-3 text-surface-500" />
                </a>
              )}
              <button
                type="button"
                onClick={() => setSection(item.id)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  section === item.id
                    ? item.danger
                      ? "bg-error-500/10 text-error-400"
                      : "bg-brand-500/10 text-brand-400"
                    : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                }`}
              >
                {item.icon}
                {item.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
      {/* Desktop: sidebar + content */}
      <div className="flex gap-6">
        {/* Sidebar nav — desktop only */}
        <nav className="hidden lg:block w-[220px] shrink-0">
          <div className="sticky top-6 space-y-0.5">
            {navItems.map((item, _i) => (
              <React.Fragment key={item.id}>
                {item.danger && (
                  <>
                    <a
                      href={`/app/clubs/${slug}/queues/${queueSlug}/flow-editor`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                    >
                      <Workflow className="size-4" />
                      Flow Editor
                      <ExternalLink className="size-3 text-surface-500 ml-auto" />
                    </a>
                    <div className="my-2 border-t border-separator" />
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSection(item.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    section === item.id
                      ? item.danger
                        ? "bg-error-500/10 text-error-400"
                        : "bg-brand-500/10 text-brand-400"
                      : item.danger
                        ? "text-error-400/60 hover:text-error-400 hover:bg-error-500/5"
                        : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/50"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        </nav>

        {/* Content panel */}
        <div className="flex-1 min-w-0">
          <div
            className={`rounded-xl ${
              section === "danger"
                ? "border border-error-500/20 bg-error-500/5 p-5"
                : "bg-surface-800/50 border border-surface-700 p-5"
            }`}
          >
            <div className="flex items-center gap-2 mb-4">
              <h2
                className={`text-sm font-bold uppercase tracking-wider ${
                  section === "danger" ? "text-error-400" : "text-white"
                }`}
              >
                {sectionTitles[section]}
              </h2>
              <button
                type="button"
                onClick={() => setHelpSection(section)}
                className="p-1 rounded-md text-surface-500 hover:text-brand-400 hover:bg-surface-700/40 transition-colors"
                aria-label={`Help for ${sectionTitles[section]}`}
              >
                <HelpCircle className="size-3.5" />
              </button>
            </div>
            {sectionRenderers[section]()}
          </div>
        </div>
      </div>
      {/* Documentation drawer */}
      <Drawer open={helpSection !== null} onClose={() => setHelpSection(null)}>
        <DrawerHeader subtitle={helpSection ? sectionTitles[helpSection] : undefined}>
          Documentation
        </DrawerHeader>
        <DrawerBody>{helpSection && sectionDocs[helpSection]}</DrawerBody>
      </Drawer>
      {/* Delete confirmation modal */}
      {showDeleteModal && clubId && (
        <Modal open={true} onClose={() => setShowDeleteModal(false)} className="!max-w-sm">
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-error-500/10 border border-error-500/20">
                <AlertTriangle className="size-5 text-error-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Delete Queue</h2>
            </div>
            <p className="text-sm text-surface-400">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-white">{queue.name}</span>? This will remove all
              queue data, stats, and match history associated with it.
            </p>
          </div>
          <ModalFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              leftIcon={
                deleteMutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="size-4" />
                )
              }
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ queueId, clubId })}
              className="!bg-error-500 hover:!bg-error-600"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Queue"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}
